/**
 * Returns a stable empty promotion config when no promotion is available.
 *
 * @returns {Object} Normalized empty promotion configuration.
 */
function EmptyPromotionConfig() {
    return {
        maxFreeAlbums: 0,
        requiredPurchases: 0,
        autoUnlockEnabled: false,
        autoUnlockThreshold: 0,
        maxAlbumsPerBatch: 0,
    };
}

/**
 * Converts API or cart values into a safe number.
 *
 * @param {*} value Original value.
 * @param {number} fallback Fallback value used when the original value is invalid.
 * @returns {number} Safe numeric value.
 */
function ToNumber(value, fallback = 0) {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
}

/**
 * Parses the Promotions payload when it arrives as a JSON string.
 * Invalid JSON is safely converted to an empty object.
 *
 * @param {string|Object|null} payload Promotions payload from Sivex/API/cart.
 * @returns {Object} Parsed payload object.
 */
function ParseJsonPayload(payload = null) {
    if (!payload) {
        return {};
    }

    if (typeof payload === "string") {
        try {
            return JSON.parse(payload) || {};
        } catch (error) {
            return {};
        }
    }

    return typeof payload === "object" ? payload : {};
}

/**
 * Normalizes the albumPromotion node from the Promotions JSON into the shape used by SUF.
 *
 * @param {string|Object|null} payload Promotions payload from API or cart.
 * @returns {Object} Normalized album promotion configuration.
 */
function NormalizePromotionConfig(payload = null) {
    const promotionPayload = ParseJsonPayload(payload);
    const albumPromotion = promotionPayload?.albumPromotion || {};

    if (!Object.keys(albumPromotion).length) {
        return EmptyPromotionConfig();
    }

    const autoUnlock = albumPromotion?.autoUnlock || {};
    const transaction = albumPromotion?.transaction || {};

    return {
        maxFreeAlbums: ToNumber(albumPromotion?.maxFreeAlbums, 0),
        requiredPurchases: ToNumber(albumPromotion?.requiredPurchases, 0),
        autoUnlockEnabled: autoUnlock?.enabled === true,
        autoUnlockThreshold: ToNumber(autoUnlock?.threshold, 0),
        maxAlbumsPerBatch: ToNumber(transaction?.maxAlbumsPerBatch, 0),
    };
}

/**
 * Checks whether the promotion config has the minimum values required to run.
 *
 * @param {Object} config Normalized promotion configuration.
 * @returns {boolean} True when the promotion can operate.
 */
function HasPromotionConfig(config = {}) {
    return (
        ToNumber(config?.requiredPurchases, 0) > 0 &&
        ToNumber(config?.autoUnlockThreshold, 0) > 0 &&
        ToNumber(config?.maxAlbumsPerBatch, 0) > 0
    );
}

/**
 * Searches for the promotion configuration inside the sivexPayData array.
 *
 * @param {Array} sivexPayData Quote response grouped by packages.
 * @returns {Object} Found normalized configuration, or an empty configuration.
 */
function GetPromotionConfigFromSivexData(sivexPayData = []) {
    if (!Array.isArray(sivexPayData)) {
        return EmptyPromotionConfig();
    }

    for (const item of sivexPayData) {
        const promotionConfig = NormalizePromotionConfig(
            item?.Promotions || item?.promotions || item
        );

        if (HasPromotionConfig(promotionConfig)) {
            return promotionConfig;
        }
    }

    return EmptyPromotionConfig();
}

/**
 * Recovers the promotion configuration from a cart item.
 * Persisted cart fields are used first, then sivexPayData, then raw Promotions.
 *
 * @param {Object|null} item Cart item.
 * @returns {Object} Normalized promotion configuration.
 */
function GetPromotionConfigFromCartItem(item = null) {
    if (!item) {
        return EmptyPromotionConfig();
    }

    const cartConfig = {
        maxFreeAlbums: ToNumber(item?.PromotionMaxFreeAlbums, 0),
        requiredPurchases: ToNumber(item?.PromotionRequiredPurchases, 0),
        autoUnlockEnabled: item?.PromotionAutoUnlockEnabled === true,
        autoUnlockThreshold: ToNumber(item?.PromotionAutoUnlockThreshold, 0),
        maxAlbumsPerBatch: ToNumber(item?.PromotionMaxAlbumsPerBatch, 0),
    };

    if (HasPromotionConfig(cartConfig)) {
        return cartConfig;
    }

    const sivexConfig = GetPromotionConfigFromSivexData(
        item?.sivexPayDataPaid || item?.sivexPayData || []
    );

    if (HasPromotionConfig(sivexConfig)) {
        return sivexConfig;
    }

    return NormalizePromotionConfig(item?.Promotions || item?.promotions);
}

/**
 * Scans the loaded albums to find an available promotion configuration before a cart item exists.
 *
 * @param {Array} albums Albums stored in Vuex.
 * @returns {Object} Found normalized configuration, or an empty configuration.
 */
function FindPromotionConfigInAlbums(albums = []) {
    if (!Array.isArray(albums)) {
        return EmptyPromotionConfig();
    }

    const queue = [...albums];
    const maxNodesToScan = 500;
    let scannedNodes = 0;

    while (queue.length && scannedNodes < maxNodesToScan) {
        const current = queue.shift();
        scannedNodes++;

        if (!current || typeof current !== "object") {
            continue;
        }

        const cartConfig = GetPromotionConfigFromCartItem(current);
        if (HasPromotionConfig(cartConfig)) {
            return cartConfig;
        }

        const directConfig = NormalizePromotionConfig(
            current?.Promotions || current?.promotions || current?.Promotion
        );
        if (HasPromotionConfig(directConfig)) {
            return directConfig;
        }

        if (Array.isArray(current?.sivexPayData)) {
            const sivexConfig = GetPromotionConfigFromSivexData(current.sivexPayData);
            if (HasPromotionConfig(sivexConfig)) {
                return sivexConfig;
            }
        }

        Object.keys(current).forEach((key) => {
            const value = current[key];

            if (value && typeof value === "object") {
                queue.push(value);
            }
        });
    }

    return EmptyPromotionConfig();
}

export {
    EmptyPromotionConfig,
    ToNumber,
    ParseJsonPayload,
    NormalizePromotionConfig,
    HasPromotionConfig,
    GetPromotionConfigFromSivexData,
    GetPromotionConfigFromCartItem,
    FindPromotionConfigInAlbums,
};

export default {
    EmptyPromotionConfig,
    ToNumber,
    ParseJsonPayload,
    NormalizePromotionConfig,
    HasPromotionConfig,
    GetPromotionConfigFromSivexData,
    GetPromotionConfigFromCartItem,
    FindPromotionConfigInAlbums,
};
